import React from 'react';

/**
 * Terms of Service page component for Raidu
 * Provides the terms and conditions for using Raidu's website and services.
 * Styled to match the site's modern, gradient aesthetic and Inter font.
 */
const TermsOfService: React.FC = () => {
  return (
    <div className="bg-gradient-to-b from-[#f8f8ff] to-[#f4f4fa] min-h-screen">
      <div className="max-w-4xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
        {/* Hero section with gradient text */}
        <div className="mb-16 text-center">
          <h1 className="text-5xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600">
            Terms of Service
          </h1>
          <p className="text-gray-600 text-lg font-light">
            Last Updated: May 6, 2025
          </p>
        </div>
        {/* Decorative element */}
        <div className="relative mb-12">
          <div className="absolute -top-6 -left-6 w-24 h-24 rounded-full bg-blue-100 opacity-50 blur-xl"></div>
          <div className="absolute -bottom-6 -right-6 w-24 h-24 rounded-full bg-purple-100 opacity-50 blur-xl"></div>
          <div className="relative z-10 h-0.5 w-full bg-gradient-to-r from-blue-200 via-indigo-300 to-purple-200"></div>
        </div>
        <div className="prose prose-lg max-w-none">
          {/* Introduction */}
          <div className="mb-12">
            <h2 id="introduction" className="text-3xl font-semibold text-gray-900 mb-6 flex items-center">
              <span className="bg-blue-100 text-blue-800 w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm font-bold">1</span>
              Introduction
            </h2>
            <p>
              Welcome to Raidu! These Terms of Service ("Terms") govern your access to and use of our website and services. By accessing or using our website, you agree to comply with and be bound by these Terms. If you do not agree, please do not use our services.
            </p>
          </div>
          {/* Use of Services */}
          <div className="mb-12">
            <h2 id="use-of-services" className="text-3xl font-semibold text-gray-900 mb-6 flex items-center">
              <span className="bg-blue-100 text-blue-800 w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm font-bold">2</span>
              Use of Services
            </h2>
            <p>
              You agree to use our website and services only for lawful purposes and in accordance with these Terms. You must not use our services:
            </p>
            <ul>
              <li>In any way that violates applicable laws or regulations</li>
              <li>To exploit, harm, or attempt to exploit or harm others</li>
              <li>To transmit any unsolicited or unauthorized advertising or promotional material</li>
              <li>To interfere with or disrupt the operation of our website or services</li>
            </ul>
          </div>
          {/* Account Registration */}
          <div className="mb-12">
            <h2 id="account-registration" className="text-3xl font-semibold text-gray-900 mb-6 flex items-center">
              <span className="bg-blue-100 text-blue-800 w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm font-bold">3</span>
              Account Registration
            </h2>
            <p>
              To access certain features, you may need to register an account. You agree to provide accurate information and to keep it updated. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.
            </p>
          </div>
          {/* Intellectual Property */}
          <div className="mb-12">
            <h2 id="intellectual-property" className="text-3xl font-semibold text-gray-900 mb-6 flex items-center">
              <span className="bg-blue-100 text-blue-800 w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm font-bold">4</span>
              Intellectual Property
            </h2>
            <p>
              All content on this website, including text, graphics, logos, and software, is the property of Raidu Inc. or its licensors and is protected by copyright and other intellectual property laws. You may not reproduce, distribute, or create derivative works without our explicit permission.
            </p>
          </div>
          {/* Disclaimers */}
          <div className="mb-12">
            <h2 id="disclaimers" className="text-3xl font-semibold text-gray-900 mb-6 flex items-center">
              <span className="bg-blue-100 text-blue-800 w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm font-bold">5</span>
              Disclaimers
            </h2>
            <p>
              Our website and services are provided on an "as is" and "as available" basis. We make no warranties, express or implied, regarding the operation or availability of our services, or the accuracy, completeness, or reliability of any content.
            </p>
          </div>
          {/* Limitation of Liability */}
          <div className="mb-12">
            <h2 id="limitation-of-liability" className="text-3xl font-semibold text-gray-900 mb-6 flex items-center">
              <span className="bg-blue-100 text-blue-800 w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm font-bold">6</span>
              Limitation of Liability
            </h2>
            <p>
              To the fullest extent permitted by law, Raidu Inc. and its affiliates shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of our website or services.
            </p>
          </div>
          {/* Indemnification */}
          <div className="mb-12">
            <h2 id="indemnification" className="text-3xl font-semibold text-gray-900 mb-6 flex items-center">
              <span className="bg-blue-100 text-blue-800 w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm font-bold">7</span>
              Indemnification
            </h2>
            <p>
              You agree to indemnify, defend, and hold harmless Raidu Inc. and its affiliates from any claims, damages, or expenses arising from your use of our website or violation of these Terms.
            </p>
          </div>
          {/* Changes to Terms */}
          <div className="mb-12">
            <h2 id="changes-to-terms" className="text-3xl font-semibold text-gray-900 mb-6 flex items-center">
              <span className="bg-blue-100 text-blue-800 w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm font-bold">8</span>
              Changes to Terms
            </h2>
            <p>
              We may update these Terms from time to time. Changes will be posted on this page with an updated "Last Updated" date. Your continued use of our website after changes constitutes acceptance of the new Terms.
            </p>
          </div>
          {/* Governing Law */}
          <div className="mb-12">
            <h2 id="governing-law" className="text-3xl font-semibold text-gray-900 mb-6 flex items-center">
              <span className="bg-blue-100 text-blue-800 w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm font-bold">9</span>
              Governing Law
            </h2>
            <p>
              These Terms are governed by the laws of the State of Delaware, United States, without regard to its conflict of law provisions.
            </p>
          </div>
          {/* Contact Us */}
          <div className="mb-12">
            <h2 id="contact-us" className="text-3xl font-semibold text-gray-900 mb-6 flex items-center">
              <span className="bg-blue-100 text-blue-800 w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm font-bold">10</span>
              Contact Us
            </h2>
            <p>
              If you have any questions about these Terms, please contact us at:
            </p>
            <p>
              Raidu Inc.<br />
              271 Little Falls Dr.<br />
              Wilmington, Delaware 19808<br />
              United States<br />
              Email: legal@raidu.com
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
